import { Link } from 'react-router-dom';
import { Heart, Clock, Users, ChefHat, Star } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

export const RecipeCard = ({ recipe, onFavoriteToggle, isFavorite }) => {
    const totalTime = recipe.prep_time + recipe.cook_time;
    
    // Random accent color for variety
    const accentColors = [
        { border: 'border-green-500', shadow: 'shadow-[6px_6px_0px_0px_hsl(150,100%,40%)]', text: 'text-green-600', bg: 'bg-green-100' },
        { border: 'border-orange-500', shadow: 'shadow-[6px_6px_0px_0px_hsl(25,100%,50%)]', text: 'text-orange-600', bg: 'bg-orange-100' },
        { border: 'border-purple-500', shadow: 'shadow-[6px_6px_0px_0px_hsl(270,80%,50%)]', text: 'text-purple-600', bg: 'bg-purple-100' },
        { border: 'border-cyan-500', shadow: 'shadow-[6px_6px_0px_0px_hsl(190,100%,45%)]', text: 'text-cyan-600', bg: 'bg-cyan-100' },
        { border: 'border-pink-500', shadow: 'shadow-[6px_6px_0px_0px_hsl(330,80%,55%)]', text: 'text-pink-600', bg: 'bg-pink-100' },
    ];
    
    // Get consistent color based on recipe id
    const colorIndex = recipe.id ? recipe.id.charCodeAt(0) % accentColors.length : 0;
    const accent = accentColors[colorIndex];
    
    const getCategoryLabel = (category) => {
        const labels = {
            'breakfast': 'Déjeuner',
            'lunch': 'Dîner',
            'dinner': 'Souper',
            'soups': 'Soupes',
            'appetizers': 'Entrées',
            'salads': 'Salades',
            'sides': 'Accompagnements',
            'dessert': 'Dessert',
            'snack': 'Collation',
            'rescue': 'Recette secours',
            'base': 'Les bases',
            'basics': 'Les bases'
        };
        return labels[category] || category;
    };

    // Get all categories (support both old single and new multiple format)
    const getCategories = () => {
        if (recipe.categories && Array.isArray(recipe.categories) && recipe.categories.length > 0) {
            return recipe.categories;
        }
        return recipe.category ? [recipe.category] : [];
    };

    const getDifficultyStyle = (difficulty) => {
        const styles = {
            'facile': 'bg-green-100 text-green-700 border-green-400',
            'moyen': 'bg-orange-100 text-orange-700 border-orange-400',
            'difficile': 'bg-purple-100 text-purple-700 border-purple-400'
        };
        return styles[difficulty] || styles['facile'];
    };
    
    return (
        <Link to={`/recipes/${recipe.id}`} data-testid={`recipe-card-${recipe.id}`}>
            <Card className={`group overflow-hidden bg-card rounded-3xl border-2 ${accent.border} ${accent.shadow} transition-all duration-300 hover:-translate-y-2 hover:rotate-1`}>
                <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                        src={recipe.image_url || 'https://images.pexels.com/photos/4122953/pexels-photo-4122953.jpeg?w=800'}
                        alt={recipe.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    
                    {recipe.is_premium && (
                        <div className="absolute top-3 left-3">
                            <span className="premium-badge flex items-center gap-1">
                                <Star className="w-3 h-3" />
                                Premium
                            </span>
                        </div>
                    )}
                    
                    {/* Solo badge */}
                    {recipe.solo_tips && recipe.solo_tips.length > 0 && (
                        <div className={`absolute ${recipe.is_premium ? 'top-12' : 'top-3'} left-3`}>
                            <span className="inline-flex items-center gap-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-2.5 py-1 rounded-full text-xs font-bold shadow-lg">
                                👤 Solo
                            </span>
                        </div>
                    )}
                    
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onFavoriteToggle && onFavoriteToggle(recipe.id);
                        }}
                        className={`absolute top-3 right-3 p-2.5 rounded-full border-2 border-foreground transition-all duration-200 shadow-pop hover:shadow-pop-hover hover:-translate-y-0.5 ${
                            isFavorite 
                                ? 'bg-pink-500 text-white' 
                                : 'bg-white text-foreground hover:bg-pink-500 hover:text-white'
                        }`}
                        data-testid={`favorite-btn-${recipe.id}`}
                    >
                        <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                    </button>
                    
                    {/* Price tag - Bottom left */}
                    <div className="absolute bottom-3 left-3">
                        <span className="price-tag text-base">
                            {recipe.cost_per_serving.toFixed(2)}$/portion
                        </span>
                    </div>
                    
                    {/* Time - Bottom right */}
                    <div className="absolute bottom-3 right-3">
                        <span className="bg-white/95 text-foreground px-3 py-1.5 rounded-full font-bold text-sm border-2 border-foreground flex items-center gap-1.5">
                            <Clock className="w-4 h-4" />
                            {totalTime} min
                        </span>
                    </div>
                </div>
                
                <CardContent className="p-5">
                    {/* Badges */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                        {getCategories().slice(0, 2).map((cat, idx) => (
                            <span key={cat} className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${idx === 0 ? `${accent.bg} ${accent.text} ${accent.border}` : 'bg-muted text-muted-foreground border-muted'}`}>
                                {getCategoryLabel(cat)}
                            </span>
                        ))}
                        {getCategories().length > 2 && (
                            <span className="px-2 py-1 rounded-full text-xs font-bold bg-muted text-muted-foreground">
                                +{getCategories().length - 2}
                            </span>
                        )}
                        <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${getDifficultyStyle(recipe.difficulty)}`}>
                            {recipe.difficulty}
                        </span>
                    </div>
                    
                    {/* Title */}
                    <h3 className={`font-heading font-black text-xl text-foreground mb-2 line-clamp-2 group-hover:${accent.text} transition-colors`}>
                        {recipe.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                        {recipe.description}
                    </p>
                    
                    {/* Footer */}
                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                            <Users className="w-4 h-4" />
                            <span className="font-medium">{recipe.servings} portions</span>
                        </div>
                        <div className={`flex items-center gap-1.5 ${accent.text} font-bold`}>
                            <Heart className="w-4 h-4 fill-current" />
                            {recipe.likes_count}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
};

export default RecipeCard;
